/*
 * Collection description -- show a few lines, put the rest in a panel.
 *
 * Two jobs, and the first is the one that matters: decide whether the button
 * is needed at all. Liquid can only guess from the character count, which is
 * blind to the width it will be read at -- the same paragraph overflows three
 * lines on a phone and fits on a desktop. Here the clamped element can simply
 * be measured, so the guess is corrected against what actually rendered.
 *
 * The panel is filled from the paragraph already on the page rather than
 * carrying its own copy. One copy in the document: nothing duplicated for a
 * crawler to weigh, and nothing sent twice to a visitor who never opens it.
 */
class CollectionDescription extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-collection-desc-body]');
    this.button = this.querySelector('[data-collection-desc-more]');
    this.panel = this.querySelector('[data-collection-desc-panel]');
    this.target = this.querySelector('[data-collection-desc-target]');
    if (!this.body || !this.button || !this.panel) return;

    this.button.addEventListener('click', () => this.open());
    this.querySelector('[data-collection-desc-close]')?.addEventListener('click', () => this.close());

    // A click landing on the dialog element itself rather than its contents is
    // the backdrop being clicked -- the backdrop is part of the element.
    this.panel.addEventListener('click', (event) => {
      if (event.target === this.panel) this.close();
    });

    this.sync();

    // Re-measured on resize: a paragraph that fits at full width may not once
    // the window is narrowed, and the clamp changes with the breakpoint too.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.body);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* Is anything actually hidden? scrollHeight is the full text, clientHeight
     the clamped box. The 2px allows for sub-pixel rounding, which otherwise
     reports a one-line paragraph as overflowing by a fraction and leaves a
     button that opens a panel showing exactly what is already on screen. */
  sync() {
    const clipped = this.body.scrollHeight - this.body.clientHeight > 2;
    this.button.hidden = !clipped;
  }

  open() {
    if (this.target && !this.target.hasChildNodes()) {
      this.target.append(...Array.from(this.body.cloneNode(true).childNodes));
      this.buildSections();
    }
    if (typeof this.panel.showModal === 'function') this.panel.showModal();
    else this.panel.setAttribute('open', '');
  }

  /* Long descriptions are a wall of text in a phone-sized panel, so each
     section heading in the copy becomes a tap-to-open row. Only the panel's
     copy is rearranged -- the paragraph on the page, the one crawlers read,
     is left exactly as written. The section level is the highest heading that
     appears at least twice, since the copy mixes h2 and h3 freely. */
  buildSections() {
    const root = this.target;
    const level = ['H2', 'H3', 'H4'].find(
      (tag) => Array.from(root.children).filter((el) => el.tagName === tag).length >= 2
    );
    if (!level) return;

    let content = null;
    for (const el of Array.from(root.children)) {
      if (el.tagName === level) {
        const details = document.createElement('details');
        details.className = 'collection-desc__section';

        const summary = document.createElement('summary');
        summary.className = 'collection-desc__section-summary';
        const title = document.createElement('span');
        title.innerHTML = el.innerHTML;

        // Same caret icon as Horizon's accordion blocks (assets/icon-caret.svg).
        const icon = document.createElement('span');
        icon.className = 'svg-wrapper icon-caret collection-desc__section-icon';
        icon.innerHTML =
          '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>';

        summary.append(title, icon);
        content = document.createElement('div');
        content.className = 'collection-desc__section-content';
        details.append(summary, content);
        el.replaceWith(details);
      } else if (content) {
        content.append(el);
      }
    }
  }

  close() {
    if (typeof this.panel.close === 'function') this.panel.close();
    else this.panel.removeAttribute('open');
  }
}

if (!customElements.get('collection-description')) {
  customElements.define('collection-description', CollectionDescription);
}
